import { chain, claw, fist, groundRing, slash, sprite, star } from '../kit';
import { C, type Pix } from '../pix';
import { registerIcons } from '../store';

/**
 * หัตถ์ล็อกโซ่ทาง (ทางโคมเหล็ก, yang): the road-wardens' holds. Bare hands, iron cuffs and chain, the
 * wardens' iron lantern and their red-corded token; warm lamp-gold for force, cold iron for the bind.
 */

const IRON = '#8a94a6';
const IRON_HI = '#d0d8e4';
const LAMP = '#ffcf5a';
const GLOW = '#fff0a8';
const CORD = '#d8452a';
const WOOD = '#7a4a2a';
const SKIN = C.skin;

/** The wardens' iron road-lantern (9×13): a hook, an iron cage, a lit core. */
function lantern(p: Pix, x: number, y: number): void {
  sprite(
    p,
    x,
    y,
    ['....i....', '...i.i...', '..iiiii..', '.iiiiiii.', '.igllgi.', '.illlli.', '.igllgi.', '.illlli.', '.iiiiiii.', '..iiiii..', '...ccc...', '...c.c...', '....c....'],
    { i: IRON, l: LAMP, g: GLOW, c: CORD },
  );
}

/** An open iron cuff (ring with a hinge). */
function cuff(p: Pix, cx: number, cy: number, r: number): void {
  p.ring(cx, cy, r, r * 0.8, IRON, 0, Math.PI * 2);
  p.ring(cx, cy, r - 1, r * 0.8 - 1, IRON_HI, Math.PI, Math.PI * 1.6);
  p.rect(cx + r - 1, cy - 1, 3, 3, IRON);
}

registerIcons('skill', {
  // The wrist lock: a hand clamps the foe's wrist and twists.
  'ah-grip': (p) => {
    p.rect(2, 15, 26, 5, C.steelMid).rect(2, 15, 26, 1, C.steel);
    fist(p, 9, 11, SKIN);
    p.ring(15, 16, 11, 9, LAMP, Math.PI * 1.15, Math.PI * 1.75);
    p.poly([
      [24, 6],
      [28, 9],
      [23, 11],
    ], LAMP);
    p.rect(4, 22, 2, 2, GLOW).rect(25, 22, 2, 2, GLOW);
  },
  // Seized and thrown down: a body arcs over and lands hard.
  'ah-throw': (p) => {
    p.ring(16, 18, 11, 11, LAMP, Math.PI * 1.05, Math.PI * 1.85);
    sprite(p, 4, 5, ['..hh.', '..hh.', 'bbbbb', '.bbb.', '.b.b.', 'b...b'], { h: SKIN, b: IRON_HI }, { k: 2, flipY: true });
    p.rect(2, 27, 18, 2, WOOD);
    for (const [x, y] of [
      [5, 24],
      [11, 22],
      [17, 24],
    ] as const)
      p.rect(x, y, 1, 2, GLOW);
  },
  // The chain is thrown and locks a cuff round its mark.
  'ah-chain': (p) => {
    chain(p, 3, 26, 20, 9, IRON);
    cuff(p, 23, 8, 5);
    fist(p, 1, 20, SKIN, { k: 1 });
    p.rect(27, 16, 2, 1, GLOW).rect(18, 3, 2, 1, GLOW);
  },
  // Two fingers strike a pressure point: the point lights and holds.
  'ah-seal': (p) => {
    sprite(
      p,
      3,
      12,
      ['..........ssssssss', '.........sssssssss', 'sssssssssssss.....', 'ssssssssssssssssss', 'sssssssssssss.....', '.ssssssssss.......'],
      { s: SKIN },
    );
    star(p, 25, 14, 4, GLOW);
    p.ring(25, 14, 6, 6, LAMP, 0, Math.PI * 2);
  },
  // A low sweeping kick round the ground; dust kicked up.
  'ah-sweep': (p) => {
    groundRing(p, 16, 22, 13, 6, LAMP);
    slash(p, 16, 22, 12, Math.PI * 0.05, Math.PI * 0.95, GLOW, 3);
    p.thick(19, 4, 15, 14, IRON_HI, 3);
    p.thick(15, 14, 8, 18, IRON_HI, 3);
    p.rect(4, 17, 5, 3, WOOD);
    for (const [x, y] of [
      [4, 15],
      [27, 16],
      [8, 12],
    ] as const)
      p.rect(x, y, 2, 1, WOOD);
  },
  // Questioning by fist: three knuckle blows, the first a lamp-gold flash.
  'ah-interrogate': (p) => {
    fist(p, 3, 11, SKIN);
    star(p, 20, 9, 3, GLOW);
    star(p, 24, 17, 2, LAMP);
    star(p, 21, 24, 2, LAMP);
    p.rect(16, 15, 3, 1, LAMP).rect(16, 18, 2, 1, LAMP);
  },
  // The warrant raised: a wooden token with a red cord, lamp light behind it.
  'ah-decree': (p) => {
    p.ring(17, 15, 11, 11, LAMP, 0, Math.PI * 2);
    p.rect(12, 5, 10, 19, WOOD);
    p.rect(13, 6, 8, 17, '#a86a3a');
    p.rect(14, 8, 6, 1, LAMP).rect(14, 11, 6, 1, LAMP).rect(16, 13, 2, 7, LAMP).rect(14, 16, 6, 1, LAMP);
    p.rect(16, 24, 2, 3, CORD).rect(15, 27, 4, 2, CORD);
  },
  // The great seizing hand closes from above; chain cuffs wait below.
  'ah-imperial': (p) => {
    lantern(p, 23, 3);
    claw(p, 5, 3, LAMP, GLOW);
    chain(p, 4, 26, 28, 26, IRON);
    cuff(p, 11, 21, 4);
    cuff(p, 21, 21, 4);
    star(p, 16, 15, 2, GLOW);
  },
});
